import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, BACKEND_URL } from "../lib/api";
import { PageHeader, Card, Button, Input, Select, Label, EmptyState } from "../components/ui-kit";
import { SearchSelect } from "../components/SearchSelect";
import { Plus, Upload, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { logger } from "../lib/logger";

export default function CustomersPage() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "manager";
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    const [c, u, pl] = await Promise.all([
      api.get("/customers"),
      canManage ? api.get("/users") : Promise.resolve({ data: [] }),
      api.get("/price-lists"),
    ]);
    setRows(c.data);
    setUsers(u.data);
    setPriceLists(pl.data);
  };
  useEffect(() => {
    load();
    // Initial fetch only; `load` closure depends on stable `canManage` boolean which
    // is derived from auth context and does not change during the page's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (form, id) => {
    try {
      if (id) await api.put(`/customers/${id}`, form);
      else await api.post("/customers", form);
      toast.success("Saved");
      setOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };
  const del = async (id) => {
    if (!window.confirm("Delete customer?")) return;
    await api.delete(`/customers/${id}`);
    load();
  };

  const uploadExcel = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/customers/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Upload complete — created ${data.created}, updated ${data.updated}`);
      if (data.errors?.length) toast.warning(`${data.errors.length} rows had issues`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const filtered = rows.filter((r) =>
    q ? [r.name, r.code, r.owner_name, r.mobile, r.gst, r.city].some((v) => (v || "").toLowerCase().includes(q.toLowerCase())) : true
  );

  return (
    <div>
      <PageHeader
        title="Customers"
        subtitle="Manage dealers & distributors. Use 'Bulk template' to download a sheet pre-filled with a sales-person dropdown."
        actions={
          canManage && (
            <>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers…" className="w-56" />
              <DownloadLinks />
              <label className="inline-flex items-center gap-2 cursor-pointer bg-transparent text-[#1E3F20] border border-[#1E3F20] hover:bg-[#F2EDE4] rounded-lg px-4 py-2 font-semibold text-sm transition-colors">
                <Upload className="w-4 h-4" /> Upload Excel
                <input type="file" accept=".xlsx" className="hidden" data-testid="customers-upload" onChange={(e) => e.target.files?.[0] && uploadExcel(e.target.files[0])} />
              </label>
              <Button
                onClick={() => {
                  setEditing(null);
                  setOpen(true);
                }}
                data-testid="new-customer-btn"
              >
                <Plus className="w-4 h-4" /> New customer
              </Button>
            </>
          )
        }
      />

      {open && (
        <CustomerForm
          users={users}
          priceLists={priceLists}
          initial={editing}
          onClose={() => {
            setOpen(false);
            setEditing(null);
          }}
          onSave={(f) => save(f, editing?.id)}
        />
      )}

      <Card className="p-0 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-8">
            <EmptyState title="No customers" description="Add a customer or upload an Excel sheet to get started." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[#F9F8F6] border-b border-[#E5E2DC]">
                <tr className="text-left text-[11px] uppercase tracking-widest text-[#5C6B5E]">
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Owner</th>
                  <th className="px-4 py-3">Mobile</th>
                  <th className="px-4 py-3">GST</th>
                  <th className="px-4 py-3">City</th>
                  <th className="px-4 py-3">Pin</th>
                  <th className="px-4 py-3">Sales person</th>
                  {canManage && <th className="px-4 py-3"></th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const assigned = users.find((u) => u.id === r.assigned_user_id);
                  return (
                    <tr key={r.id} className="border-b border-[#E5E2DC] hover:bg-[#F2EDE4]/60">
                      <td className="px-4 py-3 font-mono text-xs">{r.code}</td>
                      <td className="px-4 py-3 font-semibold">{r.name}</td>
                      <td className="px-4 py-3">{r.owner_name}</td>
                      <td className="px-4 py-3">{r.mobile}</td>
                      <td className="px-4 py-3 font-mono text-xs">{r.gst}</td>
                      <td className="px-4 py-3">{r.city}</td>
                      <td className="px-4 py-3 font-mono text-xs">{r.pin_code}</td>
                      <td className="px-4 py-3">{assigned?.name || "—"}</td>
                      {canManage && (
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => {
                              setEditing(r);
                              setOpen(true);
                            }}
                            className="text-xs px-2.5 py-1 rounded hover:bg-[#F2EDE4] mr-1"
                          >
                            Edit
                          </button>
                          <button onClick={() => del(r.id)} className="text-red-600 p-1.5 hover:bg-red-50 rounded">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function DownloadLinks() {
  const download = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error("download failed:", err);
      toast.error("Download failed");
    }
  };
  return (
    <>
      <Button variant="outline" onClick={() => download("/customers/bulk-template.xlsx", "dealers_bulk_template.xlsx")} data-testid="bulk-template-btn">
        <Download className="w-4 h-4" /> Bulk template
      </Button>
      <Button variant="outline" onClick={() => download("/customers/template.xlsx", "customers_template.xlsx")}>
        <Download className="w-4 h-4" /> Template
      </Button>
      <Button variant="outline" onClick={() => download("/customers/export.xlsx", "customers.xlsx")}>
        <Download className="w-4 h-4" /> Export
      </Button>
    </>
  );
}

function CustomerForm({ users, priceLists = [], initial, onClose, onSave }) {
  const [form, setForm] = useState({
    code: "",
    name: "",
    owner_name: "",
    mobile: "",
    gst: "",
    email: "",
    address: "",
    city: "",
    state: "",
    pin_code: "",
    assigned_user_id: "",
    default_price_list_id: "",
    ship_to_addresses: [],
    ...(initial || {}),
  });
  const eligibleAssignees = users.filter((u) =>
    ["sales", "sales_manager", "manager", "rm_and_above", "director"].includes(u.role),
  );
  const shipToList = Array.isArray(form.ship_to_addresses) ? form.ship_to_addresses : [];

  const setShipTo = (i, val) =>
    setForm((f) => ({
      ...f,
      ship_to_addresses: shipToList.map((v, idx) => (idx === i ? val : v)),
    }));
  const addShipTo = () =>
    setForm((f) => ({ ...f, ship_to_addresses: [...shipToList, ""] }));
  const rmShipTo = (i) =>
    setForm((f) => ({ ...f, ship_to_addresses: shipToList.filter((_, idx) => idx !== i) }));

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      assigned_user_id: form.assigned_user_id || null,
      default_price_list_id: form.default_price_list_id || "",
      ship_to_addresses: shipToList.map((s) => (s || "").trim()).filter(Boolean),
    });
  };
  return (
    <Card className="mb-6">
      <h3 className="font-heading text-xl font-bold mb-4">{initial ? "Edit" : "New"} customer</h3>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Customer code</Label>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required data-testid="customer-code" />
        </div>
        <div>
          <Label>Customer name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="customer-name" />
        </div>
        <div>
          <Label>Owner name</Label>
          <Input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} />
        </div>
        <div>
          <Label>Mobile number</Label>
          <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
        </div>
        <div>
          <Label>GST number</Label>
          <Input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} />
        </div>
        <div>
          <Label>Email</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div className="sm:col-span-2">
          <Label>Address</Label>
          <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </div>
        <div>
          <Label>City</Label>
          <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
        </div>
        <div>
          <Label>State</Label>
          <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
        </div>
        <div>
          <Label>Pin Code</Label>
          <Input value={form.pin_code || ""} onChange={(e) => setForm({ ...form, pin_code: e.target.value })} />
        </div>
        <div>
          <Label>Default price list</Label>
          <Select
            value={form.default_price_list_id || ""}
            onChange={(e) => setForm({ ...form, default_price_list_id: e.target.value })}
            data-testid="customer-default-pricelist"
          >
            <option value="">— None —</option>
            {priceLists.map((pl) => (
              <option key={pl.id} value={pl.id}>
                {pl.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label>Assigned owner (Sales / SM / Manager / RM / Director)</Label>
          <SearchSelect
            value={form.assigned_user_id || ""}
            onChange={(v) => setForm({ ...form, assigned_user_id: v })}
            testId="customer-assigned"
            placeholder="Search user by name, email or role…"
            options={eligibleAssignees.map((u) => ({
              value: u.id,
              label: u.name,
              sublabel: `${u.role} — ${u.email}`,
              keywords: [u.role, u.email, u.team_code].filter(Boolean),
            }))}
          />
        </div>
        <div className="sm:col-span-2">
          <Label>Saved ship-to / delivery points</Label>
          <div className="space-y-2">
            {shipToList.length === 0 && (
              <div className="text-xs text-[#5C6B5E] italic">No saved delivery points yet — orders will use free-text ship-to.</div>
            )}
            {shipToList.map((v, i) => (
              <div key={`ship-${i}`} className="flex gap-2">
                <Input
                  value={v}
                  onChange={(e) => setShipTo(i, e.target.value)}
                  placeholder={`e.g. Warehouse #${i + 1}, Sector, Town`}
                  data-testid={`customer-shipto-${i}`}
                />
                <button type="button" onClick={() => rmShipTo(i)} className="text-red-600 hover:bg-red-50 rounded p-2">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <Button variant="outline" type="button" onClick={addShipTo} data-testid="customer-add-shipto">
              <Plus className="w-4 h-4" /> Add delivery point
            </Button>
          </div>
        </div>
        <div className="sm:col-span-2 flex justify-end gap-2">
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" data-testid="customer-submit">
            {initial ? "Save changes" : "Create customer"}
          </Button>
        </div>
      </form>
    </Card>
  );
}
